import "@components/ag-grid/styles/AgGrid.css";
import {
  AllCommunityModule,
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  GridReadyEvent,
  ModuleRegistry,
  RowClickedEvent,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import React, { forwardRef, useMemo } from "react";
import { clsxm } from "../../utils/clxm/clxm";

import { enrichAgGridColumnDefs } from "./enrichColumnDefsForNumericLayout";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface AgGridReactTableProps {
  rowData: any[];
  columnDefs: (ColDef | ColGroupDef)[];
  defaultColDef?: any;
  onGridReady?: (params: GridReadyEvent) => void;
  onRowClicked?: (event: RowClickedEvent) => void;
  onCellClicked?: (event: CellClickedEvent) => void;
  onBodyScroll?: (params: any) => void;
  animateRows?: boolean;
  headerHeight?: number;
  rowHeight?: number;
  suppressCellFocus?: boolean;
  getRowId?: (params: any) => string;
  getRowClass?: (params: any) => string;
  className?: string;
  style?: React.CSSProperties;
  theme?: "legacy";
  error?: string;
  compact?: boolean;
  centerAlignHeaders?: boolean;
}

const AgGridReactTable = forwardRef<AgGridReact, AgGridReactTableProps>(
  (
    {
      rowData,
      columnDefs,
      defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        suppressSizeToFit: false,
        floatingFilter: true,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
      },
      onGridReady,
      onRowClicked,
      onCellClicked,
      onBodyScroll,
      animateRows = true,
      headerHeight = 40,
      rowHeight = 48,
      suppressCellFocus = true,
      getRowId,
      getRowClass,
      className = "ag-theme-alpine overflow-hidden",
      style = { height: "100%", width: "100%" },
      theme = "legacy",
      error,
      compact = false,
      centerAlignHeaders = false,
    },
    ref,
  ) => {
    const mergedColumnDefs = useMemo(
      () => enrichAgGridColumnDefs(columnDefs),
      [columnDefs],
    );

    return (
      <div
        className={clsxm(
          "relative",
          className,
          compact && "ag-grid-compact",
          centerAlignHeaders && "ag-grid-center-headers",
          "rounded-none",
        )}
        style={style}
      >
        <AgGridReact
          theme={theme}
          ref={ref}
          rowData={rowData}
          columnDefs={mergedColumnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          onRowClicked={onRowClicked}
          onCellClicked={onCellClicked}
          onBodyScroll={onBodyScroll}
          animateRows={animateRows}
          headerHeight={headerHeight}
          rowHeight={rowHeight}
          suppressCellFocus={suppressCellFocus}
          getRowId={getRowId}
          getRowClass={getRowClass}
          suppressHorizontalScroll={false}
          domLayout="normal"
          suppressColumnVirtualisation={true}
        />
        {error && (
          <div className="absolute inset-0 bg-white bg-opacity-95 flex items-center justify-center z-10">
            <div className="text-center p-6 max-w-md">
              <div className="mb-4">
                <span className="material-symbols-outlined text-text-status-red text-4xl">
                  error
                </span>
              </div>
              <p className="text-lg font-semibold text-text-status-red mb-2">
                Something went wrong
              </p>
              <p className="text-text-status-red text-sm mb-4">{error}</p>
            </div>
          </div>
        )}
      </div>
    );
  },
);

AgGridReactTable.displayName = "AgGridReactTable";

export default AgGridReactTable;
